import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaSmile, FaQuestionCircle, FaHands, FaRegCalendarAlt, FaHistory, FaCheckCircle, FaChevronLeft } from 'react-icons/fa';
import { HiSparkles as HiSparklesIcon } from 'react-icons/hi';
import { FiEdit3, FiCalendar, FiArrowLeft, FiCheck } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import ModuleHeader from "../../moduleHeader";

const defaultPackageData = {
  user: {
    first_name: "Kullanıcı",
    last_name: ""
  },
  package: {
    name: "Mini Journal",
    description: "Günlük düşüncelerinizi kaydedin. İyi hissettiğiniz şeyleri, zorlandıklarınızı ve minnettar olduklarınızı yazın.",
    image_url: "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=800&q=80"
  }
};

const journalPrompts = [
  {
    id: 1,
    question: "Bugün iyi hissettiğim bir şey...",
    icon: FaSmile,
    color: "from-emerald-400/20 to-teal-500/20",
    textColor: "text-emerald-600",
    iconColor: "text-emerald-500",
    placeholder: "Bugün sizi mutlu eden bir anı paylaşın..."
  },
  {
    id: 2,
    question: "Bugün zorlandığım bir şey...",
    icon: FaQuestionCircle,
    color: "from-orange-400/20 to-red-500/20",
    textColor: "text-orange-600",
    iconColor: "text-orange-500",
    placeholder: "Bugün sizi düşündüren veya yoran ne oldu?"
  },
  {
    id: 3,
    question: "Şu anda minnettar olduğum bir şey...",
    icon: FaHands,
    color: "from-purple-400/20 to-pink-500/20",
    textColor: "text-purple-600",
    iconColor: "text-purple-500",
    placeholder: "Küçük bir teşekkür her şeyi değiştirebilir..."
  },
  {
    id: 4,
    question: "Günün Genel Özeti & Serbest Yazım",
    icon: FiEdit3,
    color: "from-blue-400/20 to-indigo-500/20",
    textColor: "text-blue-600",
    iconColor: "text-blue-500",
    placeholder: "Bugünle ilgili içinden geçen başka ne varsa buraya not düşebilirsin..."
  }
];

export default function MiniJournal({ id: propId, moduleId, isOwner }) {
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const actualId = routeId || propId;

  const [entries, setEntries] = useState({});
  const [currentDate, setCurrentDate] = useState(new Date().toISOString().split('T')[0]);
  const [packageData] = useState(defaultPackageData);
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);
  const [journalHistory, setJournalHistory] = useState(() => {
    const saved = localStorage.getItem('miniJournalHistory');
    return saved ? JSON.parse(saved) : {};
  });

  useEffect(() => {
    const todayEntries = journalHistory[currentDate] || {};
    setEntries(todayEntries);
  }, [currentDate, journalHistory]);

  const handleEntryChange = (promptId, value) => {
    const newEntries = { ...entries, [promptId]: value };
    setEntries(newEntries);

    const updatedHistory = { ...journalHistory, [currentDate]: newEntries };
    setJournalHistory(updatedHistory);
    localStorage.setItem('miniJournalHistory', JSON.stringify(updatedHistory));
  };

  const getFormattedDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const completedCount = Object.values(entries).filter(entry => entry?.trim()).length;
  const progress = (completedCount / journalPrompts.length) * 100;
  const allCompleted = completedCount === journalPrompts.length;

  return (
    <div className="w-full h-screen min-h-screen flex items-stretch justify-stretch sm:items-center sm:justify-center bg-[#f8f9fc] relative">
      <div className="flex flex-col items-center w-full min-h-full bg-white shadow-2xl border-x border-gray-100 rounded-none sm:max-w-[420px] sm:h-[800px] sm:rounded-3xl relative overflow-y-auto no-scrollbar">

        {/* ModuleHeader */}
        <div className="relative z-30 w-full mb-0">
          <ModuleHeader
            userName={packageData.user?.first_name + " " + packageData.user?.last_name}
            subtitle={packageData.package?.name}
            title={packageData.package?.name}
            description={packageData.package?.description}
            backgroundImage={packageData.package?.image_url}
            onExpandedChange={setIsHeaderExpanded}
            showToggle={true}
            autoHeight={true}
          />
        </div>

        {/* Floating Back Button */}
        <button
          onClick={() => navigate(`/${actualId}`)}
          className="absolute top-4 left-4 z-[40] w-10 h-10 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center text-white border border-white/20 hover:bg-white/20 transition-all active:scale-90"
        >
          <FiArrowLeft size={20} />
        </button>

        <div className="w-full px-6 py-8 pb-32 flex-1">
          {/* Top Bar: Date & Progress */}
          <div className="bg-white/50 backdrop-blur-sm rounded-3xl border border-gray-100 p-5 shadow-sm mb-8 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <span className="text-[10px] uppercase font-bold text-gray-400 tracking-widest pl-1">Seçili Tarih</span>
                <div className="flex items-center gap-2 mt-1 relative group cursor-pointer bg-gray-50 hover:bg-gray-100 px-3 py-1.5 rounded-xl transition-colors border border-gray-100">
                  <FiCalendar className="text-[#3DA7B5] w-4 h-4" />
                  <span className="text-sm font-bold text-gray-800">{getFormattedDate(currentDate)}</span>
                  <FiEdit3 className="ml-auto text-gray-400 w-3 h-3 group-hover:text-[#3DA7B5] transition-colors" />
                  <input
                    type="date"
                    value={currentDate}
                    onChange={(e) => setCurrentDate(e.target.value)}
                    className="absolute inset-0 opacity-0 cursor-pointer w-full"
                  />
                </div>
              </div>
              <div className="flex flex-col items-end">
                <span className="text-[10px] uppercase font-bold text-gray-400 tracking-widest pr-1">İlerleme</span>
                <span className="text-sm font-black text-[#3DA7B5] mt-1">%{Math.round(progress)}</span>
              </div>
            </div>

            <div className="relative h-2 w-full bg-gray-100 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                className="absolute top-0 left-0 h-full bg-gradient-to-r from-[#3DA7B5] to-[#5ABCC8] rounded-full"
              />
            </div>
          </div>

          {/* Journal Prompts */}
          <div className="space-y-6 mb-8">
            {journalPrompts.map((prompt, idx) => {
              const entry = entries[prompt.id] || '';
              const isCompleted = entry.trim().length > 0;
              const IconComp = prompt.icon;

              return (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  key={prompt.id}
                  className={`group relative bg-white border rounded-[2rem] p-6 transition-all duration-300 ${isCompleted ? 'border-[#3DA7B5]/30 shadow-md shadow-[#3DA7B5]/5' : 'border-gray-100 shadow-sm'
                    }`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`p-2.5 rounded-2xl bg-gradient-to-br ${prompt.color} ${prompt.textColor}`}>
                        <IconComp size={22} />
                      </div>
                      <h3 className="text-gray-900 font-bold text-sm tracking-tight">{prompt.question}</h3>
                    </div>
                    {isCompleted && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="bg-[#3DA7B5] text-white p-1 rounded-full shadow-lg shadow-[#3DA7B5]/30"
                      >
                        <FiCheck size={12} strokeWidth={4} />
                      </motion.div>
                    )}
                  </div>

                  <div className="relative">
                    <textarea
                      value={entry}
                      onChange={(e) => handleEntryChange(prompt.id, e.target.value)}
                      placeholder={prompt.placeholder}
                      className="w-full px-5 py-4 bg-gray-50/50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-[#3DA7B5]/10 focus:border-[#3DA7B5] focus:bg-white transition-all resize-none text-[13px] text-gray-700 leading-relaxed font-light min-h-[120px] outline-none"
                    />
                    <div className="absolute right-4 bottom-4 opacity-10 group-focus-within:opacity-30 transition-opacity">
                      <FiEdit3 size={18} />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Completion Celebration */}
          <AnimatePresence>
            {allCompleted && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                className="bg-gradient-to-br from-[#3DA7B5] to-[#5ABCC8] rounded-[2rem] p-8 text-center text-white shadow-xl shadow-[#3DA7B5]/30"
              >
                <div className="w-16 h-16 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center mx-auto mb-4 border border-white/30">
                  <HiSparklesIcon className="text-3xl" />
                </div>
                <h4 className="text-lg font-black mb-2 tracking-tight">Harika Bir Gün!</h4>
                <p className="text-white/80 text-xs font-medium leading-relaxed">
                  Düşüncelerini kaleme alarak bugün kendine en iyi hediyeyi verdin.
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* History Section Modernized */}
          {Object.keys(journalHistory).length > 0 && (
            <div className="mt-12 space-y-4">
              <div className="flex items-center gap-2 px-1">
                <FaHistory className="text-gray-400 text-sm" />
                <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest">Geçmiş Anılar</h4>
              </div>
              <div className="flex gap-3 overflow-x-auto no-scrollbar pb-4 -mx-2 px-2">
                {Object.keys(journalHistory)
                  .sort((a, b) => new Date(b) - new Date(a))
                  .slice(0, 10)
                  .map((date) => {
                    const dateEntries = journalHistory[date];
                    const entryCount = Object.values(dateEntries).filter(e => e?.trim()).length;
                    const isSelected = date === currentDate;

                    return (
                      <motion.button
                        whileTap={{ scale: 0.95 }}
                        key={date}
                        onClick={() => setCurrentDate(date)}
                        className={`flex-shrink-0 w-24 p-3 rounded-2xl border transition-all flex flex-col items-center gap-1.5 ${isSelected
                          ? 'bg-[#3DA7B5] border-[#3DA7B5] text-white shadow-lg shadow-[#3DA7B5]/30'
                          : 'bg-white border-gray-100 text-gray-600 hover:border-gray-200 shadow-sm'
                          }`}
                      >
                        <span className={`text-[10px] font-bold ${isSelected ? 'text-white/80' : 'text-gray-400'}`}>
                          {new Date(date).getDate()}
                        </span>
                        <span className="text-[11px] font-black">
                          {new Date(date).toLocaleDateString('tr-TR', { month: 'short' })}
                        </span>
                        <div className={`mt-1 flex items-center gap-0.5 text-[8px] font-bold uppercase ${isSelected ? 'text-white/60' : 'text-[#3DA7B5]'}`}>
                          {entryCount}/{journalPrompts.length}
                        </div>
                      </motion.button>
                    );
                  })}
              </div>
            </div>
          )}
        </div>

        {/* Floating Add Entry Trigger or similar could go here if needed */}
      </div>
    </div>
  );
}
